import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Loader2, ScanLine, IdCard, Flag, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { homeFor } from "@/lib/format";
import { Logo, useSettings } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { startGoogleLogin } from "@/pages/AuthCallback";

const HERO = "/images/hero-kart.jpg";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const settings = useSettings();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      navigate(homeFor(u.role));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#0F2042] lg:block">
        <img src={HERO} alt="Karting" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#0F2042]/70" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <Logo settings={settings} dark className="h-12 w-12" />
            <div>
              <div className="font-display text-lg font-bold" data-testid="login-brand-name">Passrace</div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-400">MSP Italia · Movimento Sportivo Popolare</div>
            </div>
          </div>
          <div className="max-w-lg fade-up">
            <div className="mb-4 h-1 w-16 bg-amber-500" />
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">Registro Nazionale Licenze Sportive</h1>
            <p className="mt-4 text-lg text-blue-100/80">Settori Motori · Karting e Automobilismo</p>
            <div className="mt-10 grid grid-cols-3 gap-4 text-sm">
              {[[IdCard, "Licenze emesse dalla sede nazionale"], [ScanLine, "Verifica QR in tempo reale"], [Flag, "Riconoscimento tra settori"]].map(([I, t]) => (
                <div key={t} className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <I className="mb-2 h-5 w-5 text-amber-400" />
                  <div className="text-blue-50/90">{t}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-blue-100/50">Accesso riservato a operatori autorizzati e atleti tesserati.</div>
        </div>
      </div>
      <div className="flex items-center justify-center bg-white px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-blue-800 hover:underline" data-testid="login-back-home-link"><ArrowLeft className="h-4 w-4" />Calendario gare</Link>
          <div className="mb-8 lg:hidden"><Logo settings={settings} className="h-12 w-12" /></div>
          <div className="eyebrow mb-2">Area riservata</div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Accedi</h2>
          <p className="mt-2 text-sm text-slate-600">Inserisci le credenziali fornite dalla sede nazionale MSP Italia.</p>
          <div className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="email">Email</label>
              <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" />
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700" htmlFor="password">Password</label>
                <Link to="/password-dimenticata" className="text-xs font-medium text-blue-700 hover:text-blue-900" data-testid="forgot-password-link">Password dimenticata?</Link>
              </div>
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" />
            </div>
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="h-11 w-full bg-[#0F2042] text-base hover:bg-[#1E3A8A]" data-testid="login-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </Button>
            <div className="flex items-center gap-3 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" />oppure<div className="h-px flex-1 bg-slate-200" /></div>
            <Button type="button" variant="outline" onClick={startGoogleLogin} className="h-11 w-full text-base" data-testid="google-login-btn">
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" /><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A10.96 10.96 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" /></svg>
              Accedi con Google
            </Button>
            <p className="text-center text-xs text-slate-500">Con Google accedono solo gli account già abilitati dalla sede nazionale.</p>
          </div>
          <div className="mt-8 flex items-start gap-2 text-xs text-slate-500">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            Connessione cifrata. I dati personali sono trattati secondo il GDPR e visibili solo agli operatori autorizzati.
          </div>
        </form>
      </div>
    </div>
  );
}
